import {
    AddressType,
    AnalysisVisitStatus,
    BusinessActivity,
    CatalogFile,
    City,
    CivilStatus,
    Colonia,
    Dependency,
    DocumentConfig,
    FinancialCompany,
    IndividualPower,
    JointPower,
    LegalIncidentStatus,
    LegalIncidentStatusSubcategory,
    MatrimonialRegime,
    Motorbike,
    OperationManager,
    PaymentMethod,
    PepType,
    PersonType,
    Product,
    PublicOffice,
    ReasonRejectionApplication,
    ReasonRejectionDocument,
    ReasonUninterested,
    ReferenceType,
    Relationship,
    RepresentationType,
    ResourceUse,
    SourceRegion,
    Stage,
    State,
    Status,
    StatusConfig,
    TaxSystem,
    GuaranteeType,
    PropertyType,
    GuaranteeConfig,
    ReasonRejectionRevision,
    ExternalRating,
    CreditValidationCatalog,
    MotorbikeCatalog,
    PlatformCatalog,
    TabsBofuConfig,
    ResourceUseConfig,
    Visitador,
    GuaranteeTypeDetailConfig,
    SowingTypeConfig,
    PropertyLocationConfig,
    AgriculturalCycleTypeConfig,
    HydrologicalRegimeTypeConfig,
    AgriculturalCosts,
    IncomeDocumentsConfig,
    PayrollAgreement,
    IdentificationTypeConfig,
    VisitAnalysisConfig,
} from "@interfaces/catalogs";
import { baseApi } from "./baseApi";
import { UUID } from "crypto";
import { Locality } from "@interfaces/postalCodes";
import { PaginatedObject, Response, ResponsePagination } from "@interfaces/response";
import { CompanyStageOwner } from "@interfaces/users";

const catalogsApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        getStages: build.query<Array<Stage>, void>({
            query: () => "catalogos/stage",
            transformResponse: (response: ResponsePagination<Stage>) => {
                return response.data.data;
            },
        }),
        getStatus: build.query<ResponsePagination<Status>, string>({
            query: (params: string) => ({
                url: `catalogos/status?${params}`,
            }),
        }),
        getPersonTypes: build.query<Array<PersonType>, void | string>({
            query: (params) => ({
                url: `catalogos/person_type?${params}`,
                params: {
                    size: 50,
                },
            }),
            transformResponse: (response: ResponsePagination<PersonType>) => {
                return response.data.data;
            },
        }),
        getReasonUninterested: build.query<Array<ReasonUninterested>, void>({
            query: () => ({
                url: "catalogos/razon_uninterested",
            }),
            transformResponse: (response: ResponsePagination<ReasonUninterested>) => {
                return response.data.data;
            },
        }),
        getReasonRejectionDocs: build.query<Array<ReasonRejectionDocument>, void>({
            query: () => ({
                url: "catalogos/reason_rejection_document",
            }),
            transformResponse: (response: ResponsePagination<ReasonRejectionDocument>) => {
                return response.data.data;
            },
        }),
        getReasonRejectionApp: build.query<Array<ReasonRejectionApplication>, void>({
            query: () => ({
                url: "catalogos/reason_rejection",
            }),
            transformResponse: (response: ResponsePagination<ReasonRejectionApplication>) => {
                return response.data.data;
            },
        }),
        getFileCategories: build.query<Array<CatalogFile>, string>({
            query: (params) => ({
                url: `catalogos/file_category?${params}`,
                // params: {
                //     size: 100,
                // },
            }),
            transformResponse: (response: ResponsePagination<CatalogFile>) => {
                return response.data.data;
            },
        }),
        getStates: build.query<Array<State>, void>({
            query: () => ({
                url: "catalogos/state",
                params: {
                    size: 50,
                },
            }),
            transformResponse: (response: ResponsePagination<State>) => {
                return response.data.data;
            },
        }),
        getState: build.query<State, number>({
            query: (stateId) => ({
                url: `catalogos/state/${stateId}`,
            }),
            transformResponse: (response: Response<State>) => {
                return response.data;
            },
        }),
        getCitiesByState: build.query<Array<City>, number>({
            query: (stateId) => ({
                url: `catalogos/state/${stateId}/city`,
                params: {
                    size: 1000,
                },
            }),
            transformResponse: (response: ResponsePagination<City>) => {
                return response.data.data;
            },
        }),
        getLocalitiesByState: build.query<Array<Locality>, number>({
            query: (stateId) => ({
                url: `catalogos/locality`,
                params: {
                    q: `state_id:${stateId}`,
                    order_by: `name:asc`,
                    size: 1000,
                },
            }),
            transformResponse: (response: ResponsePagination<Locality>) => {
                return response.data.data;
            },
        }),
        getAddressTypes: build.query<Array<AddressType>, void>({
            query: () => ({
                url: "catalogos/address_type",
                params: {
                    size: 20,
                },
            }),
            transformResponse: (response: ResponsePagination<AddressType>) => {
                return response.data.data;
            },
        }),
        getCity: build.query<City, number>({
            query: (cityId) => ({
                url: `catalogos/city/${cityId}`,
            }),
            transformResponse: (response: Response<City>) => {
                return response.data;
            },
        }),
        getColonia: build.query<Colonia, number>({
            query: (coloniaId) => ({
                url: `catalogos/colonia/${coloniaId}`,
            }),
            transformResponse: (response: Response<Colonia>) => {
                return response.data;
            },
        }),
        getStatusConfig: build.query<Array<StatusConfig>, { productId: UUID; taxSystemId: UUID }>({
            query: ({ productId, taxSystemId }) => ({
                url: "catalogos/status_config",
                params: {
                    product_id: productId,
                    tax_system_id: taxSystemId,
                },
            }),
            transformResponse: (response: ResponsePagination<StatusConfig>) => {
                return response.data.data;
            },
        }),
        getCompanyStageOwners: build.query<Array<CompanyStageOwner>, string>({
            query: (params) => ({
                url: `catalogos/company_stage_owner_config?${params}`,
            }),
            transformResponse: (response: ResponsePagination<CompanyStageOwner>) => {
                return response.data.data;
            },
        }),
        postCompanyStageOwner: build.mutation<CompanyStageOwner, { companyId: UUID; stageId: UUID; userBpmId: UUID }>({
            query: ({ companyId, stageId, userBpmId }) => ({
                url: "catalogos/company_stage_owner_config",
                method: "POST",
                body: {
                    company_id: companyId,
                    stage_id: stageId,
                    user_bpm_id: userBpmId,
                },
            }),
            transformResponse: (response: Response<CompanyStageOwner>) => {
                return response.data;
            },
        }),
        deleteCompanyStageOwner: build.mutation<void, UUID>({
            query: (id) => ({
                url: `catalogos/company_stage_owner_config/${id}`,
                method: "DELETE",
            }),
        }),
        getLegalIncidentsStatus: build.query<Array<LegalIncidentStatus>, any>({
            query: () => ({
                url: "catalogos/legal_incident_status",
            }),
            transformResponse: (response: ResponsePagination<LegalIncidentStatus>) => {
                return response.data.data;
            },
        }),
        getAnalysisVisitStatus: build.query<Array<AnalysisVisitStatus>, any>({
            query: () => ({
                url: "catalogos/visit_analysis_status",
            }),
            transformResponse: (response: ResponsePagination<AnalysisVisitStatus>) => {
                return response.data.data;
            },
        }),
        getOperationManagers: build.query<Array<OperationManager>, void>({
            query: () => ({
                url: `catalogos/operation_managers`,
            }),
            transformResponse: (response: ResponsePagination<OperationManager>) => {
                return response.data.data;
            },
        }),
        getRepresentationTypeLegal: build.query<Array<RepresentationType>, void>({
            query: () => ({
                url: `catalogos/representation_type_legal_representative`,
            }),
            transformResponse: (response: ResponsePagination<RepresentationType>) => {
                return response.data.data;
            },
        }),
        getBusinessActivity: build.query<BusinessActivity, UUID>({
            query: (businessActivityId) => ({
                url: `catalogos/business_activity/${businessActivityId}`,
            }),
            transformResponse: (response: Response<BusinessActivity>) => {
                return response.data;
            },
        }),
        getBusinessActivities: build.query<Array<BusinessActivity>, void>({
            query: () => ({
                url: `catalogos/business_activity`,
            }),
            transformResponse: (response: ResponsePagination<BusinessActivity>) => {
                return response.data.data;
            },
        }),
        getDocumentConfig: build.query<Array<DocumentConfig>, string>({
            query: (params) => ({
                url: `catalogos/document_config?${params}`,
            }),
            transformResponse: (response: ResponsePagination<DocumentConfig>) => {
                return response.data.data;
            },
        }),
        getPaymentMethods: build.query<Array<PaymentMethod>, string | void>({
            query: (params) => ({
                url: `catalogos/payment_method/${params}`,
            }),
            transformResponse: (response: ResponsePagination<PaymentMethod>) => {
                return response.data.data;
            },
        }),
        getDependencies: build.query<Array<Dependency>, void>({
            query: () => ({
                url: `catalogos/dependencies`,
            }),
            transformResponse: (response: ResponsePagination<Dependency>) => {
                return response.data.data;
            },
        }),
        getPublicOffices: build.query<Array<PublicOffice>, void>({
            query: () => ({
                url: "catalogos/public_office",
            }),
            transformResponse: (response: ResponsePagination<PublicOffice>) => {
                return response.data.data;
            },
        }),
        getPepTypes: build.query<Array<PepType>, string>({
            query: (params) => ({
                url: `catalogos/pep_type?${params}`,
            }),
            transformResponse: (response: ResponsePagination<PepType>) => {
                return response.data.data;
            },
        }),
        getRelationships: build.query<Array<Relationship>, void>({
            query: () => ({
                url: `catalogos/relationship`,
            }),
            transformResponse: (response: ResponsePagination<PepType>) => {
                return response.data.data;
            },
        }),
        getCivilStatus: build.query<Array<CivilStatus>, void>({
            query: () => ({
                url: `catalogos/civil_status`,
            }),
            transformResponse: (response: ResponsePagination<CivilStatus>) => {
                return response.data.data;
            },
        }),
        getMatrimonialRegimes: build.query<Array<MatrimonialRegime>, void>({
            query: () => ({
                url: `catalogos/matrimonial_regimes`,
            }),
            transformResponse: (response: ResponsePagination<MatrimonialRegime>) => {
                return response.data.data;
            },
        }),
        getProducts: build.query<Array<Product>, string>({
            query: (queryParams) => ({
                url: `catalogos/products?${queryParams}`,
            }),
            transformResponse: (response: ResponsePagination<Product>) => {
                return response.data.data;
            },
        }),
        getProduct: build.query<Product, UUID>({
            query: (productId) => ({
                url: `catalogos/products/${productId}`,
            }),
            transformResponse: (response: Response<Product>) => {
                return response.data;
            },
        }),
        getUseResources: build.query<Array<ResourceUse>, void>({
            query: () => ({
                url: "catalogos/loan_use_categories",
            }),
            transformResponse: (response: ResponsePagination<ResourceUse>) => {
                return response.data.data;
            },
        }),
        getUseResourceConfig: build.query<Array<ResourceUseConfig>, string>({
            query: (params) => ({
                url: `catalogos/loan_use_categories_config?${params}`,
            }),
            transformResponse: (response: ResponsePagination<ResourceUseConfig>) => {
                return response.data.data;
            },
        }),
        getFinancialCompanies: build.query<PaginatedObject<FinancialCompany>, string>({
            query: (params) => ({
                url: `catalogos/financial_companies?${params}`,
            }),
            transformResponse: (response: ResponsePagination<FinancialCompany>) => {
                return response.data;
            },
        }),
        getFinancialCompany: build.query<FinancialCompany, UUID>({
            query: (financialCompanyId) => ({
                url: `catalogos/financial_companies/${financialCompanyId}`,
            }),
            transformResponse: (response: Response<FinancialCompany>) => {
                return response.data;
            },
        }),
        postFinancialCompany: build.mutation<FinancialCompany, Partial<Omit<FinancialCompany, "id">>>({
            query: (payload) => ({
                url: `catalogos/financial_companies`,
                method: "POST",
                body: payload,
            }),
            transformResponse: (response: Response<FinancialCompany>) => {
                return response.data;
            },
        }),
        patchFinancialCompany: build.mutation<
            FinancialCompany,
            { financialCoId: UUID; payload: Partial<FinancialCompany> }
        >({
            query: ({ financialCoId, payload }) => ({
                url: `catalogos/financial_companies/${financialCoId}`,
                method: "PATCH",
                body: payload,
            }),
            transformResponse: (response: Response<FinancialCompany>) => {
                return response.data;
            },
        }),
        getGuaranteeTypes: build.query<Array<GuaranteeType>, void>({
            query: () => ({
                url: `catalogos/guarantee_type`,
            }),
            transformResponse: (response: ResponsePagination<GuaranteeType>) => {
                return response.data.data;
            },
        }),
        getReferenceTypes: build.query<Array<ReferenceType>, void>({
            query: () => ({
                url: "catalogos/reference_type",
            }),
            transformResponse: (response: ResponsePagination<ReferenceType>) => {
                return response.data.data;
            },
        }),
        getLegalIncidentStatusSubcategories: build.query<Array<LegalIncidentStatusSubcategory>, string>({
            query: (params) => ({
                url: `catalogos/legal_incident_status_subcategory?${params}`,
            }),
            transformResponse: (response: ResponsePagination<LegalIncidentStatusSubcategory>) => {
                return response.data.data;
            },
        }),
        getTaxSystems: build.query<Array<TaxSystem>, string>({
            query: (params) => ({
                url: `catalogos/tax_systems?${params}`,
            }),
            transformResponse: (response: ResponsePagination<TaxSystem>) => {
                return response.data.data;
            },
        }),
        getTaxSystem: build.query<TaxSystem, UUID>({
            query: (taxSystemId: UUID) => ({
                url: `catalogos/tax_systems/${taxSystemId}`,
            }),
            transformResponse: (response: Response<TaxSystem>) => {
                return response.data;
            },
        }),
        getIndividualPowers: build.query<Array<IndividualPower>, string>({
            query: (params) => ({
                url: `catalogos/individual_powers_catalog?${params}`,
            }),
            transformResponse: (response: ResponsePagination<IndividualPower>) => {
                return response.data.data;
            },
        }),
        getJointPowers: build.query<Array<JointPower>, string>({
            query: (params) => ({
                url: `catalogos/joint_powers_catalog?${params}`,
            }),
            transformResponse: (response: ResponsePagination<JointPower>) => {
                return response.data.data;
            },
        }),
        getSourceRegion: build.query<Array<SourceRegion>, void>({
            query: () => ({
                url: `catalogos/source_region`,
            }),
            transformResponse: (response: ResponsePagination<SourceRegion>) => {
                return response.data.data;
            },
        }),
        getPropertyTypes: build.query<Array<PropertyType>, void>({
            query: () => ({
                url: `catalogos/property_type`,
            }),
            transformResponse: (response: ResponsePagination<PropertyType>) => {
                return response.data.data;
            },
        }),
        getGuaranteeConfig: build.query<Array<GuaranteeConfig>, string>({
            query: (params) => ({
                url: `catalogos/guarantee_config?${params}`,
            }),
            transformResponse: (response: ResponsePagination<GuaranteeConfig>) => {
                return response.data.data;
            },
        }),
        getReasonRejectionRevision: build.query<Array<ReasonRejectionRevision>, string>({
            query: (params) => ({
                url: `catalogos/reason_rejection_applications_config?${params}`,
            }),
            transformResponse: (response: ResponsePagination<ReasonRejectionRevision>) => {
                return response.data.data;
            },
        }),
        getExternalRating: build.query<Array<ExternalRating>, string>({
            query: (params) => ({
                url: `catalogos/external_rating?${params}`,
            }),
            transformResponse: (response: ResponsePagination<ExternalRating>) => {
                return response.data.data;
            },
        }),
        getCreditValidationsCatalog: build.query<Array<CreditValidationCatalog>, string>({
            query: (params) => ({
                url: `catalogos/credit_validations?${params}`,
            }),
            transformResponse: (response: ResponsePagination<CreditValidationCatalog>) => {
                return response.data.data;
            },
        }),
        getMotorbikes: build.query<Array<Motorbike>, string | void>({
            query: (params) => ({
                url: `catalogos/motorbike${params ? `?${params}` : ""}`,
            }),
            transformResponse: (response: ResponsePagination<Motorbike>) => {
                return response.data.data;
            },
        }),
        getMotorbikeCatalog: build.query<Array<MotorbikeCatalog>, string>({
            query: (params) => ({
                url: `catalogos/motorbike?${params}`,
            }),
            transformResponse: (response: ResponsePagination<MotorbikeCatalog>) => {
                return response.data.data;
            },
        }),
        getPlatformCatalog: build.query<Array<PlatformCatalog>, string>({
            query: (params) => ({
                url: `catalogos/platform?${params}`,
            }),
            transformResponse: (response: ResponsePagination<PlatformCatalog>) => {
                return response.data.data;
            },
        }),
        getTabsBofuConfig: build.query<Array<TabsBofuConfig>, string>({
            query: (params) => ({
                url: `catalogos/tabs_bofu_config?${params}`,
            }),
            transformResponse: (response: ResponsePagination<TabsBofuConfig>) => {
                return response.data.data;
            },
        }),
        getVisitadores: build.query<Array<Visitador>, void>({
            query: () => ({
                url: `catalogos/visitador_catalog`,
            }),
            transformResponse: (response: ResponsePagination<Visitador>) => {
                return response.data.data;
            },
        }),
        getGuaranteeTypeDetailConfig: build.query<Array<GuaranteeTypeDetailConfig>, string>({
            query: (params) => ({
                url: `catalogos/guarantee_type_detail_config?${params}`,
            }),
            transformResponse: (response: ResponsePagination<GuaranteeTypeDetailConfig>) => {
                return response.data.data;
            },
        }),
        getSowingTypeConfig: build.query<Array<SowingTypeConfig>, string>({
            query: (params) => ({
                url: `catalogos/sowing_type_config?${params}`,
            }),
            transformResponse: (response: ResponsePagination<SowingTypeConfig>) => {
                return response.data.data;
            },
        }),
        getPropertyLocationConfig: build.query<Array<PropertyLocationConfig>, string>({
            query: (params) => ({
                url: `catalogos/property_location_config?${params}`,
            }),
            transformResponse: (response: ResponsePagination<PropertyLocationConfig>) => {
                return response.data.data;
            },
        }),
        getAgriculturalCycleTypeConfig: build.query<Array<AgriculturalCycleTypeConfig>, string>({
            query: (params) => ({
                url: `catalogos/agricultural_cycle_type_config?${params}`,
            }),
            transformResponse: (response: ResponsePagination<AgriculturalCycleTypeConfig>) => {
                return response.data.data;
            },
        }),
        getAgriculturalCosts: build.query<Array<AgriculturalCosts>, string>({
            query: (params) => ({
                url: `catalogos/agricultural_costs?${params}`,
            }),
            transformResponse: (response: ResponsePagination<AgriculturalCosts>) => {
                return response.data.data;
            },
        }),
        getHydrologicalRegimeTypeConfig: build.query<Array<HydrologicalRegimeTypeConfig>, string>({
            query: (params) => ({
                url: `catalogos/hydrological_regime_type_config?${params}`,
            }),
            transformResponse: (response: ResponsePagination<HydrologicalRegimeTypeConfig>) => {
                return response.data.data;
            },
        }),
        getIncomeDocumentsConfig: build.query<Array<IncomeDocumentsConfig>, string>({
            query: (params) => ({
                url: `catalogos/income_documents_config?${params}`,
            }),
            transformResponse: (response: ResponsePagination<IncomeDocumentsConfig>) => {
                return response.data.data;
            },
        }),
        getPayrollAgreement: build.query<PayrollAgreement, UUID>({
            query: (id) => ({
                url: `catalogos/payroll_agreements_catalog/${id}`,
            }),
            transformResponse: (response: Response<PayrollAgreement>) => {
                return response.data;
            },
        }),
        getIdentificationTypeConfig: build.query<Array<IdentificationTypeConfig>, string>({
            query: (params) => ({
                url: `catalogos/identification_type_config?${params}`,
            }),
            transformResponse: (response: ResponsePagination<IdentificationTypeConfig>) => {
                return response.data.data;
            },
        }),
        getVisitAnalysisConfig: build.query<VisitAnalysisConfig | undefined, string>({
            query: (params) => ({
                url: `catalogos/visit_analysis_config?${params}`,
            }),
            transformResponse: (response: ResponsePagination<VisitAnalysisConfig>) => {
                return response.data.data.at(0);
            },
        }),
    }),
});

export const {
    useGetStagesQuery,
    useGetStatusQuery,
    useGetStatesQuery,
    useLazyGetStatesQuery,
    useGetStateQuery,
    useGetCityQuery,
    useGetColoniaQuery,
    useLazyGetCitiesByStateQuery,
    useLazyGetLocalitiesByStateQuery,
    useLazyGetStatusQuery,
    useGetPersonTypesQuery,
    useLazyGetPersonTypesQuery,
    useGetReasonUninterestedQuery,
    useGetReasonRejectionDocsQuery,
    useGetReasonRejectionAppQuery,
    useLazyGetReasonRejectionAppQuery,
    useGetFileCategoriesQuery,
    useLazyGetFileCategoriesQuery,
    useGetAddressTypesQuery,
    useLazyGetAddressTypesQuery,
    useGetStatusConfigQuery,
    useLazyGetStatusConfigQuery,
    useLazyGetCompanyStageOwnersQuery,
    usePostCompanyStageOwnerMutation,
    useDeleteCompanyStageOwnerMutation,
    useGetLegalIncidentsStatusQuery,
    useLazyGetAnalysisVisitStatusQuery,
    useGetAnalysisVisitStatusQuery,
    useGetOperationManagersQuery,
    useLazyGetOperationManagersQuery,
    useLazyGetRepresentationTypeLegalQuery,
    useLazyGetBusinessActivityQuery,
    useLazyGetBusinessActivitiesQuery,
    useLazyGetDocumentConfigQuery,
    useLazyGetPaymentMethodsQuery,
    useLazyGetPublicOfficesQuery,
    useLazyGetDependenciesQuery,
    useLazyGetPepTypesQuery,
    useLazyGetRelationshipsQuery,
    useLazyGetCivilStatusQuery,
    useLazyGetMatrimonialRegimesQuery,
    useLazyGetProductsQuery,
    useLazyGetProductQuery,
    useLazyGetUseResourcesQuery,
    useLazyGetUseResourceConfigQuery,
    useLazyGetFinancialCompaniesQuery,
    useLazyGetFinancialCompanyQuery,
    usePostFinancialCompanyMutation,
    usePatchFinancialCompanyMutation,
    useLazyGetGuaranteeTypesQuery,
    useLazyGetReferenceTypesQuery,
    useGetReferenceTypesQuery,
    useLazyGetLegalIncidentStatusSubcategoriesQuery,
    useGetLegalIncidentStatusSubcategoriesQuery,
    useLazyGetTaxSystemsQuery,
    useLazyGetTaxSystemQuery,
    useLazyGetIndividualPowersQuery,
    useLazyGetJointPowersQuery,
    useLazyGetSourceRegionQuery,
    useLazyGetPropertyTypesQuery,
    useLazyGetGuaranteeConfigQuery,
    useLazyGetReasonRejectionRevisionQuery,
    useLazyGetExternalRatingQuery,
    useLazyGetCreditValidationsCatalogQuery,
    useGetMotorbikesQuery,
    useLazyGetMotorbikesQuery,
    useLazyGetMotorbikeCatalogQuery,
    useLazyGetPlatformCatalogQuery,
    useLazyGetTabsBofuConfigQuery,
    useLazyGetVisitadoresQuery,
    useLazyGetGuaranteeTypeDetailConfigQuery,
    useLazyGetSowingTypeConfigQuery,
    useLazyGetPropertyLocationConfigQuery,
    useLazyGetAgriculturalCycleTypeConfigQuery,
    useLazyGetAgriculturalCostsQuery,
    useLazyGetHydrologicalRegimeTypeConfigQuery,
    useLazyGetIncomeDocumentsConfigQuery,
    useLazyGetPayrollAgreementQuery,
    useLazyGetIdentificationTypeConfigQuery,
    useLazyGetVisitAnalysisConfigQuery,
} = catalogsApi;
